import * as S from './CoveredSection.styles'
import useScreenSize from '../../../../hooks/useScreenSize'
import PhotoBooksCoveredSection from '../../../PhotoBookHomePage/Components/CoveredSection/CoveredSection'
import startCase from 'lodash/startCase'
import { ILLUSTRATIONS } from 'mixtiles-web-common/consts/illustrations.const'
import { CDN_URL } from 'config/config'
import { usePricing } from '../../../../components/Pricing/PricingProvider'
import { useMemo } from 'react'
import { formatPrice } from '../../../../utils/utils'
import { useTranslation } from 'react-i18next'

type IllustrationNames = {
  rating: string
  satisfaction: string
  shipping: string
  support: string
}

const DEFAULT_ILLUSTRATION_NAMES: IllustrationNames = {
  rating: ILLUSTRATIONS.HOMEPAGE.RATING,
  satisfaction: ILLUSTRATIONS.HOMEPAGE.SATISFACTION,
  shipping: ILLUSTRATIONS.HOMEPAGE.SHIPPING,
  support: ILLUSTRATIONS.HOMEPAGE.SUPPORT,
}

const DEFAULT_MOBILE_ILLUSTRATION_URLS = {
  shipping: `${CDN_URL}/illustrations/homepage/Shipping.png`,
  support: `${CDN_URL}/illustrations/homepage/Support.png`,
  rated: `${CDN_URL}/illustrations/homepage/Rated.png`,
}

function CoveredSection({
  forceFreeShipping = false,
  illustrationNames = DEFAULT_ILLUSTRATION_NAMES,
  mobileIllustrationUrls = DEFAULT_MOBILE_ILLUSTRATION_URLS,
}: {
  forceFreeShipping?: boolean
  illustrationNames?: IllustrationNames
  mobileIllustrationUrls?: {
    shipping: string
    support: string
    rated: string
  }
}) {
  const { t } = useTranslation()
  const isMediumScreen = useScreenSize('md')
  const isLargeScreen = useScreenSize('lg')
  const { discounts, pricingData } = usePricing()

  const staticIllustrations = useMemo(
    () => [
      {
        name: illustrationNames.rating,
        title: t('homepage.covered.rated', {
          v1: '4.9/5',
        }),
        subtitle: t('homepage.covered.rated_subtitle', {
          v1: '120k',
        }),
      },
      {
        name: illustrationNames.satisfaction,
        title: startCase(t('pitch.icons.satisfaction.title')),
        subtitle: t('pitch.icons.satisfaction.text'),
      },
    ],
    [illustrationNames, t]
  )

  const displayIllustrations = useMemo(() => {
    const discount = discounts.value.find(
      (t) => t.discountId === 'FREE_SHIPPING_OVER'
    )
    const discountPrice =
      discount && pricingData.currency
        ? formatPrice(
            discount.shippingBuyOverData.buyOverPrice,
            pricingData.currency
          )
        : ''
    const shippingIllustration = {
      name: illustrationNames.shipping,
      title: t('homepage.covered.shipping'),
      subtitle:
        discountPrice && !forceFreeShipping
          ? t('homepage.covered.shipping_subtitle', {
              v1: discountPrice,
            })
          : t('homepage.covered.shipping_free_subtitle'),
    }
    const illustrations = [shippingIllustration, ...staticIllustrations]
    if (!isLargeScreen) {
      const SUPPORT_ILLUSTRATION = {
        name: illustrationNames.support,
        title: t('homepage.covered.support_short'),
        subtitle: t('homepage.covered.support_subtitle'),
      }
      illustrations.push(SUPPORT_ILLUSTRATION)
    }
    return illustrations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLargeScreen, discounts, illustrationNames, staticIllustrations, t])

  return isMediumScreen ? (
    <S.Container>
      <S.Title>{t('homepage.covered.title')}</S.Title>
      <S.IllustrationsContainer>
        {displayIllustrations.map(({ name, title, subtitle }) => (
          <CoveredIllustration
            key={name}
            name={name}
            title={title}
            subtitle={subtitle}
          />
        ))}
      </S.IllustrationsContainer>
    </S.Container>
  ) : (
    <PhotoBooksCoveredSection
      communicateFreeShipping={forceFreeShipping}
      illustrationUrls={mobileIllustrationUrls}
    />
  )
}

function CoveredIllustration({
  name,
  title,
  subtitle,
}: {
  name: string
  title: string
  subtitle: string
}) {
  return (
    <S.IllustrationBackground>
      <S.IllustrationContainer>
        <S.Illustration name={name} />
        <S.IllustrationTitle>{title}</S.IllustrationTitle>
        <S.IllustrationSubtitle>{subtitle}</S.IllustrationSubtitle>
      </S.IllustrationContainer>
    </S.IllustrationBackground>
  )
}

export default CoveredSection
